import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Search,
  Filter,
  Edit,
  Trash2,
  Calendar,
  MapPin,
  Home,
  History as HistoryIcon,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Building2,
  FileSpreadsheet,
  X,
  ArrowUp,
  ArrowDown,
  Plus,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

// Types
interface Unit {
  unit_id: number
  unit_ref: string
  project: {
    name: string
    address: string
    city: string
    district: string
  }
  typology: string
  floor: string
  status: 'available' | 'reserved' | 'sold' | 'unknown'
  current_price: number
  sqm_price: number | null
  private_gpa: number
  total_gpa: number
  parking: number
  listing_date: string
  sales_date?: string
  time_on_market?: number
  last_scraped_at: string
  reserved_date?: string
  reservation_count?: number
}

interface UnitFilters {
  search: string
  status: string
  projectName: string
  city: string
  district: string
  typology: string
  architect: string
  developer: string
  broker: string
  minPrice?: number
  maxPrice?: number
  minSqmPrice?: number
  maxSqmPrice?: number
  minGpa?: number
  maxGpa?: number
  dateFrom?: string
  dateTo?: string
  timeOnMarketFrom?: number
  timeOnMarketTo?: number
}

const createDefaultFilters = (): UnitFilters => ({
  search: '',
  status: '',
  projectName: '',
  city: '',
  district: '',
  typology: '',
  architect: '',
  developer: '',
  broker: '',
  minPrice: undefined,
  maxPrice: undefined,
  minSqmPrice: undefined,
  maxSqmPrice: undefined,
  minGpa: undefined,
  maxGpa: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  timeOnMarketFrom: undefined,
  timeOnMarketTo: undefined,
})

const STRING_FILTER_KEYS = [
  'search',
  'status',
  'projectName',
  'city',
  'district',
  'typology',
  'architect',
  'developer',
  'broker',
  'dateFrom',
  'dateTo',
] as const

const NUMERIC_FILTER_KEYS = [
  'minPrice',
  'maxPrice',
  'minSqmPrice',
  'maxSqmPrice',
  'minGpa',
  'maxGpa',
  'timeOnMarketFrom',
  'timeOnMarketTo',
] as const

const parseNumberParam = (value: string | null): number | undefined => {
  if (value === null || value.trim() === '') {
    return undefined
  }
  const parsed = Number(value)
  return Number.isNaN(parsed) ? undefined : parsed
}

const areFiltersEqual = (a: UnitFilters, b: UnitFilters) => {
  const keys = new Set<keyof UnitFilters>([
    ...(Object.keys(a) as Array<keyof UnitFilters>),
    ...(Object.keys(b) as Array<keyof UnitFilters>),
  ])
  for (const key of keys) {
    if (a[key] !== b[key]) {
      return false
    }
  }
  return true
}

interface SortConfig {
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
}

const parseFiltersFromSearchParams = (params: URLSearchParams): {
  filters: UnitFilters
  page: number
  sort: SortConfig
} => {
  const filters = createDefaultFilters()

  STRING_FILTER_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value !== null) {
      (filters as any)[key] = value
    }
  })

  NUMERIC_FILTER_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value !== null) {
      (filters as any)[key] = parseNumberParam(value)
    }
  })

  const pageParam = params.get('page')
  const parsedPage = pageParam ? Number(pageParam) : 1
  const page = Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1

  const sortByParam = params.get('sortBy') || 'updated_at'
  const sortOrderParam = params.get('sortOrder')
  const sort: SortConfig = {
    sortBy: sortByParam,
    sortOrder: sortOrderParam === 'ASC' || sortOrderParam === 'DESC' ? sortOrderParam : 'DESC',
  }

  return { filters, page, sort }
}

const buildSearchParamsFromState = (
  filters: UnitFilters,
  page: number,
  sort: SortConfig,
): URLSearchParams => {
  const params = new URLSearchParams()

  if (page > 1) {
    params.set('page', String(page))
  }

  if (sort.sortBy && sort.sortBy !== 'updated_at') {
    params.set('sortBy', sort.sortBy)
  }
  if (sort.sortOrder && sort.sortOrder !== 'DESC') {
    params.set('sortOrder', sort.sortOrder)
  }

  STRING_FILTER_KEYS.forEach((key) => {
    const value = filters[key]
    if (typeof value === 'string' && value.trim() !== '') {
      params.set(key, value)
    }
  })

  NUMERIC_FILTER_KEYS.forEach((key) => {
    const value = filters[key]
    if (value !== undefined && value !== null && !Number.isNaN(value)) {
      params.set(key, String(value))
    }
  })

  return params
}

export function Units() {
  const navigate = useNavigate()
  const { canExport } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<UnitFilters>(createDefaultFilters)
  const [debouncedFilters, setDebouncedFilters] = useState<UnitFilters>(createDefaultFilters)
  const [initializedFromParams, setInitializedFromParams] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(50) // Larger page size for list view
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [isExporting, setIsExporting] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null)

  const searchParamsString = useMemo(() => searchParams.toString(), [searchParams])

  // Initialize state from URL params
  useEffect(() => {
    const { filters: parsedFilters, page, sort } = parseFiltersFromSearchParams(searchParams)

    setFilters(prev => (areFiltersEqual(prev, parsedFilters) ? prev : parsedFilters))
    setDebouncedFilters(prev => (areFiltersEqual(prev, parsedFilters) ? prev : parsedFilters))
    setCurrentPage(prev => (prev === page ? prev : page))
    setSortBy(prev => (prev === sort.sortBy ? prev : sort.sortBy))
    setSortOrder(prev => (prev === sort.sortOrder ? prev : sort.sortOrder))

    if (!initializedFromParams) {
      setInitializedFromParams(true)
    }
  }, [searchParams, initializedFromParams])

  // Sync state to URL params
  useEffect(() => {
    if (!initializedFromParams) {
      return
    }

    const params = buildSearchParamsFromState(debouncedFilters, currentPage, { sortBy, sortOrder })
    const nextString = params.toString()

    if (nextString !== searchParamsString) {
      setSearchParams(params, { replace: true })
    }
  }, [debouncedFilters, currentPage, sortBy, sortOrder, initializedFromParams, searchParamsString, setSearchParams])

  // Debounce filters for API calls and reset page when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      // Reset to page 1 if filters actually changed (to avoid empty results on higher pages)
      if (!areFiltersEqual(filters, debouncedFilters)) {
        setCurrentPage(1)
      }
      setDebouncedFilters(filters)
    }, 500)

    return () => clearTimeout(timer)
  }, [filters, debouncedFilters])



  // Fetch filter options (dynamic typologies)
  const { data: filterOptions } = useQuery({
    queryKey: ['unit-filter-options'],
    queryFn: async () => {
      const response = await api.get('/units/filter-options')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Fetch unit history when a unit is selected
  const { data: unitHistory, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['unit-history', selectedUnit?.unit_id],
    queryFn: async () => {
      if (!selectedUnit?.unit_id) return null
      const response = await api.get(`/units/${selectedUnit.unit_id}/history`)
      return response.data
    },
    enabled: !!selectedUnit?.unit_id,
  })

  // Fetch units with advanced filtering and pagination
  const { data: unitsData, isLoading } = useQuery({
    queryKey: ['units', debouncedFilters, currentPage, pageSize, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams()

      // Basic filters
      if (debouncedFilters.search) params.append('search', debouncedFilters.search)
      if (debouncedFilters.status) params.append('status', debouncedFilters.status)
      if (debouncedFilters.projectName) params.append('projectName', debouncedFilters.projectName)
      if (debouncedFilters.city) params.append('city', debouncedFilters.city)
      if (debouncedFilters.district) params.append('district', debouncedFilters.district)
      if (debouncedFilters.typology) params.append('typology', debouncedFilters.typology)
      if (debouncedFilters.architect) params.append('architect', debouncedFilters.architect)
      if (debouncedFilters.developer) params.append('developer', debouncedFilters.developer)
      if (debouncedFilters.broker) params.append('broker', debouncedFilters.broker)

      // Price filters
      if (debouncedFilters.minPrice !== undefined) params.append('minPrice', debouncedFilters.minPrice.toString())
      if (debouncedFilters.maxPrice !== undefined) params.append('maxPrice', debouncedFilters.maxPrice.toString())
      if (debouncedFilters.minSqmPrice !== undefined) params.append('minSqmPrice', debouncedFilters.minSqmPrice.toString())
      if (debouncedFilters.maxSqmPrice !== undefined) params.append('maxSqmPrice', debouncedFilters.maxSqmPrice.toString())

      // GPA filters
      if (debouncedFilters.minGpa !== undefined) params.append('minGpa', debouncedFilters.minGpa.toString())
      if (debouncedFilters.maxGpa !== undefined) params.append('maxGpa', debouncedFilters.maxGpa.toString())

      // Date filters
      if (debouncedFilters.dateFrom) params.append('dateFrom', debouncedFilters.dateFrom)
      if (debouncedFilters.dateTo) params.append('dateTo', debouncedFilters.dateTo)

      // Time on market filters
      if (debouncedFilters.timeOnMarketFrom !== undefined) params.append('timeOnMarketFrom', debouncedFilters.timeOnMarketFrom.toString())
      if (debouncedFilters.timeOnMarketTo !== undefined) params.append('timeOnMarketTo', debouncedFilters.timeOnMarketTo.toString())

      // Pagination and sorting
      params.append('page', currentPage.toString())
      params.append('limit', pageSize.toString())
      params.append('sortBy', sortBy)
      params.append('sortOrder', sortOrder)

      const response = await api.get(`/units?${params}`)
      return response.data
    },
  })

  // Extract units and pagination info
  const units = unitsData?.units || []
  const pagination = unitsData?.pagination || {
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  }



  // Export to Excel
  const handleExport = async () => {
    setIsExporting(true)
    try {
      const params = new URLSearchParams()

      if (filters.search) params.append('search', filters.search)
      if (filters.status) params.append('status', filters.status)
      if (filters.projectName) params.append('projectName', filters.projectName)
      if (filters.city) params.append('city', filters.city)
      if (filters.district) params.append('district', filters.district)
      if (filters.typology) params.append('typology', filters.typology)
      if (filters.architect) params.append('architect', filters.architect)
      if (filters.developer) params.append('developer', filters.developer)
      if (filters.broker) params.append('broker', filters.broker)
      if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString())
      if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString())
      if (filters.minSqmPrice !== undefined) params.append('minSqmPrice', filters.minSqmPrice.toString())
      if (filters.maxSqmPrice !== undefined) params.append('maxSqmPrice', filters.maxSqmPrice.toString())
      if (filters.minGpa !== undefined) params.append('minGpa', filters.minGpa.toString())
      if (filters.maxGpa !== undefined) params.append('maxGpa', filters.maxGpa.toString())
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
      if (filters.dateTo) params.append('dateTo', filters.dateTo)
      if (filters.timeOnMarketFrom !== undefined) params.append('timeOnMarketFrom', filters.timeOnMarketFrom.toString())
      if (filters.timeOnMarketTo !== undefined) params.append('timeOnMarketTo', filters.timeOnMarketTo.toString())

      // Create a direct download link since the backend sends the file as binary
      const downloadUrl = `${api.defaults.baseURL}/units/export?${params}`
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', `units_export_${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  // Handle unit click to navigate to unit detail
  const handleUnitClick = (unit: Unit) => {
    navigate(`/units/${unit.unit_id}`)
  }

  const clearFilters = useCallback(() => {
    const emptyFilters = createDefaultFilters()
    setFilters(emptyFilters)
    setDebouncedFilters(emptyFilters)
    setCurrentPage(1)
    setSortBy('updated_at')
    setSortOrder('DESC')
  }, [])

  const formatCurrency = (amount: number) => {
    // Format number with Portuguese locale (space as thousands separator)
    const formattedNumber = new Intl.NumberFormat('pt-PT', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)

    return `${formattedNumber} €`
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'badge-success'
      case 'reserved': return 'badge-warning'
      case 'sold': return 'badge-info'
      default: return 'badge-gray'
    }
  }


  return (
    <div className="space-y-6">


      <div className="space-y-6 w-full px-4 sm:px-6 lg:px-8 pb-8 pt-2">
        {/* Streamlined Filter Layout */}
        <div className="space-y-4 mb-4">
          {/* Essential Filters - Always Visible */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="form-group">
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  className="form-input pl-10"
                  placeholder="Unit, project, address..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="available">Available</option>
                <option value="reserved">Reserved</option>
                <option value="sold">Sold</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Project</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by project..."
                value={filters.projectName}
                onChange={(e) => setFilters(prev => ({ ...prev, projectName: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">District</label>
              <input
                type="text"
                className="form-input"
                placeholder="Lisboa, Porto..."
                value={filters.district}
                onChange={(e) => setFilters(prev => ({ ...prev, district: e.target.value }))}
              />
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-secondary-600" />
              <h3 className="text-lg font-semibold text-secondary-900">Advanced Filters</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary flex items-center ${showFilters ? 'bg-primary-50 text-primary-700' : ''}`}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {/* Advanced Filters - Organized by Categories */}
        {showFilters && (
          <div className="border-t border-secondary-200 pt-6 mt-6">
            <div className="space-y-6">
              {/* Property Details */}
              <div>
                <h4 className="text-sm font-semibold text-secondary-700 mb-3">Property Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Typology</label>
                    <select
                      className="form-input"
                      value={filters.typology}
                      onChange={(e) => setFilters(prev => ({ ...prev, typology: e.target.value }))}
                    >
                      <option value="">All Types</option>
                      {filterOptions?.typologies?.map((type: string) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Developer</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Filter by developer..."
                      value={filters.developer}
                      onChange={(e) => setFilters(prev => ({ ...prev, developer: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Broker</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Filter by broker..."
                      value={filters.broker}
                      onChange={(e) => setFilters(prev => ({ ...prev, broker: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Architect</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Filter by architect..."
                      value={filters.architect}
                      onChange={(e) => setFilters(prev => ({ ...prev, architect: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Price Range Filters */}
              <div>
                <h4 className="text-sm font-semibold text-secondary-700 mb-3">Price Range</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Min Price (€)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={filters.minPrice ?? ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value ? Number(e.target.value) : undefined }))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max Price (€)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="No limit"
                      value={filters.maxPrice ?? ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value ? Number(e.target.value) : undefined }))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Min Price/m² (€)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={filters.minSqmPrice ?? ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, minSqmPrice: e.target.value ? Number(e.target.value) : undefined }))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max Price/m² (€)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="No limit"
                      value={filters.maxSqmPrice ?? ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxSqmPrice: e.target.value ? Number(e.target.value) : undefined }))}
                    />
                  </div>
                </div>
              </div>

              {/* Gross private area */}
              <div>
                <h4 className="text-sm font-semibold text-secondary-700 mb-3">Gross private area (m²)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Min gross private area (m²)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={filters.minGpa ?? ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, minGpa: e.target.value ? Number(e.target.value) : undefined }))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max gross private area (m²)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="No limit"
                      value={filters.maxGpa ?? ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxGpa: e.target.value ? Number(e.target.value) : undefined }))}
                    />
                  </div>
                </div>
              </div>

              {/* Sales Date Range Filters */}
              <div>
                <h4 className="text-sm font-semibold text-secondary-700 mb-3">Sales Date Range</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Sales Date From</label>
                    <input
                      type="date"
                      className="form-input"
                      value={filters.dateFrom || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Sales Date To</label>
                    <input
                      type="date"
                      className="form-input"
                      value={filters.dateTo || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end pt-4 border-t border-secondary-200">
                <button
                  onClick={clearFilters}
                  className="btn-secondary flex items-center"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Units List View */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-b border-secondary-200">
          <div className="flex items-center text-sm text-secondary-600">
            <span className="font-medium text-secondary-900 mr-1">{pagination.total}</span> units
            <span className="mx-2">•</span>
            <span>Page {pagination.page} of {pagination.totalPages}</span>
            {isLoading && <span className="ml-2 text-primary-600 text-xs font-semibold">(Updating...)</span>}
          </div>

          <div className="flex items-center gap-2">
            {canExport && (
              <>
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="btn-secondary py-1.5 h-9 text-xs flex items-center"
                >
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                  {isExporting ? 'Exporting...' : 'Export'}
                </button>

                <div className="h-6 w-px bg-secondary-200 mx-1 hidden sm:block"></div>
              </>
            )}

            <select
              className="form-input py-1.5 h-9 text-xs w-32 sm:w-40"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
            >
              <option value="created_at">Date Created</option>
              <option value="price">Price</option>
              <option value="status">Status</option>
              <option value="unit_ref">Unit Ref</option>
              <option value="listing_date">Listing Date</option>
              <option value="time_on_market">Time On Market</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
              className="btn-secondary py-1.5 h-9 w-9 p-0 flex items-center justify-center"
              title={sortOrder === 'ASC' ? 'Sort Ascending' : 'Sort Descending'}
            >
              {sortOrder === 'ASC' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
            </button>

            <button
              className="btn-primary py-1.5 h-9 text-xs flex items-center ml-1"
              onClick={() => { /* Add unit modal handler later */ }}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Unit
            </button>
          </div>
        </div>

        {/* Units Table */}
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full divide-y divide-secondary-200 table-fixed">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Unit & Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="w-40 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Details
                </th>
                <th className="w-36 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="w-28 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-32 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Time on Market
                </th>
                <th className="w-32 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Reserved Date
                </th>
                <th className="w-32 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Reservations
                </th>
                <th className="w-24 px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {units.map((unit) => (
                <tr
                  key={unit.unit_id}
                  className="hover:bg-secondary-50 cursor-pointer transition-colors"
                  onClick={() => handleUnitClick(unit)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-secondary-900">
                        {unit.unit_ref}
                      </div>
                      <div className="text-sm text-secondary-500">
                        <Building2 className="h-3 w-3 inline mr-1" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/projects/${unit.project.project_id}`)
                          }}
                          className="text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                        >
                          {unit.project.name}
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 truncate max-w-[200px] sm:max-w-xs md:max-w-sm lg:max-w-md xl:max-w-lg">
                    <div className="text-sm font-medium text-secondary-900 truncate" title={unit.project.address || `${unit.project.city}, ${unit.project.district}`}>
                      <MapPin className="h-3 w-3 inline mr-1 text-secondary-400" />
                      {unit.project.address || `${unit.project.city}, ${unit.project.district}`}
                    </div>
                    <div className="text-xs text-secondary-500 mt-1">
                      {unit.project.city} • {unit.project.district}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-secondary-900">
                      {unit.typology} • {unit.floor}º • {Math.round(unit.private_gpa || 0)}m²
                    </div>
                    <div className="text-sm text-secondary-500">
                      {unit.parking > 0 && `${unit.parking} parking`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-secondary-900">
                      {formatCurrency(unit.current_price)}
                    </div>
                    <div className="text-sm text-secondary-500">
                      €{unit.sqm_price !== null && unit.sqm_price !== undefined && typeof unit.sqm_price === 'number' ? unit.sqm_price.toFixed(0) : 'N/A'}/m²
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`status-badge ${getStatusColor(unit.status)}`}>
                      {unit.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {unit.time_on_market || 0} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {unit.reserved_date ? new Date(String(unit.reserved_date)).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {unit.reservation_count || 0} times
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleUnitClick(unit)
                        }}
                        className="text-primary-600 hover:text-primary-900"
                        title="View unit details"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <div className="loading-shimmer w-full h-12 mb-4"></div>
            <div className="loading-shimmer w-3/4 h-8 mx-auto"></div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && units.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
              <Building2 className="h-8 w-8 text-secondary-400" />
            </div>
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No units found</h3>
            <p className="text-secondary-500 mb-4">
              {pagination.total === 0 ? 'No units match your current filters' : 'Try adjusting your filters'}
            </p>
            {Object.values(filters).some(v => v) && (
              <button onClick={clearFilters} className="btn-secondary">
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-secondary-600">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={!pagination.hasPrev}
              className="btn-secondary p-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={!pagination.hasPrev}
              className="btn-secondary p-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      pageNum === pagination.page
                        ? 'bg-primary-600 text-white'
                        : 'btn-secondary hover:bg-secondary-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
              disabled={!pagination.hasNext}
              className="btn-secondary p-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCurrentPage(pagination.totalPages)}
              disabled={!pagination.hasNext}
              className="btn-secondary p-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Unit History Modal */}
      {showHistory && selectedUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-tagus-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-secondary-900">
                    Unit {selectedUnit.unit_ref} History
                  </h3>
                  <p className="text-sm text-secondary-500">{selectedUnit.project.name}</p>
                </div>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Loading State */}
              {isHistoryLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="loading-shimmer w-8 h-8 rounded-full"></div>
                  <span className="ml-3 text-secondary-600">Loading history...</span>
                </div>
              ) : unitHistory?.history?.length > 0 ? (
                /* Timeline */
                <div className="space-y-6">
                {unitHistory.history.map((entry, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-primary-600" />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="bg-secondary-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-secondary-900">
                            {new Date(entry.event_date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                          <span className={`status-badge ${getStatusColor(entry.status)}`}>
                            {entry.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-secondary-500">Price</p>
                            <p className="font-semibold text-secondary-900">
                              {formatCurrency(entry.price)}
                            </p>
                          </div>
                          <div>
                            <p className="text-secondary-500">Price/m²</p>
                            <p className="font-semibold text-secondary-900">
                              €{entry.sqm_price !== null && entry.sqm_price !== undefined && typeof entry.sqm_price === 'number' ? entry.sqm_price.toFixed(2) : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-secondary-500">Status</p>
                            <p className="font-semibold text-secondary-900">
                              {entry.status}
                            </p>
                          </div>
                          <div>
                            <p className="text-secondary-500">Available</p>
                            <p className="font-semibold text-secondary-900">
                              {entry.available ? 'Yes' : 'No'}
                            </p>
                          </div>
                        </div>

                        {/* Show changes if available */}
                        {unitHistory.diffs[index] && unitHistory.diffs[index].changes.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-secondary-200">
                            <p className="text-xs font-medium text-secondary-700 mb-2">Changes:</p>
                            <div className="flex flex-wrap gap-2">
                              {unitHistory.diffs[index].changes.map((change, changeIndex) => (
                                <div key={changeIndex} className="flex items-center space-x-2 text-xs">
                                  <span className="text-secondary-500">{change.field}:</span>
                                  <span className="text-danger-600 line-through">
                                    {change.field === 'price' ? formatCurrency(change.from) :
                                     change.field === 'sqm_price' ? `€${change.from !== null && change.from !== undefined && typeof change.from === 'number' ? change.from.toFixed(2) : 'N/A'}` :
                                     change.from}
                                  </span>
                                  <svg className="w-3 h-3 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <span className="text-success-600 font-medium">
                                    {change.field === 'price' ? formatCurrency(change.to) :
                                     change.field === 'sqm_price' ? `€${change.to !== null && change.to !== undefined && typeof change.to === 'number' ? change.to.toFixed(2) : 'N/A'}` :
                                     change.to}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
                    <HistoryIcon className="h-8 w-8 text-secondary-400" />
                  </div>
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">No history available</h3>
                  <p className="text-secondary-500">This unit doesn't have any recorded history yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
      </div>
  )
}
